import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { MapPin, ExternalLink, Sparkles, Loader2, Clock, Coffee, ArrowRight, Wand2 } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

const VIBES = [
  { id: "romantic", label: "Romantic" },
  { id: "casual", label: "Casual" },
  { id: "adventurous", label: "Adventurous" },
  { id: "upscale", label: "Upscale" },
];
const BUDGETS = [
  { id: "low", label: "$" },
  { id: "medium", label: "$$" },
  { id: "high", label: "$$$" },
];

const DatePlanner = () => {
  const [city, setCity] = useState("");
  const [vibe, setVibe] = useState("romantic");
  const [budget, setBudget] = useState("medium");
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    api.get("/date-plans").then((r) => setHistory(r.data.plans || [])).catch(() => {});
  }, []);

  const generate = async (e) => {
    e?.preventDefault();
    if (!city.trim()) return;
    setLoading(true); setPlan(null);
    try {
      const r = await api.post("/date-plan", { city: city.trim(), vibe, budget });
      setPlan(r.data);
      const h = await api.get("/date-plans");
      setHistory(h.data.plans || []);
    } catch { toast.error("Could not generate a plan — try again."); }
    setLoading(false);
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      <div className="overline mb-2">DATE NIGHT PLANNER</div>
      <h1 className="font-serif text-4xl sm:text-5xl mb-4" data-testid="planner-title">
        Build a two-spot date <span className="italic text-amber-500">she'll brag about.</span>
      </h1>
      <p className="text-neutral-400 max-w-2xl mb-8">Pick your city and vibe. Suitor plans a real venue A → transition → venue B, with map and booking links.</p>

      <form onSubmit={generate} className="card-surface p-6 md:p-8 mb-10">
        <label className="overline text-neutral-500">City</label>
        <input
          type="text"
          value={city}
          onChange={(e) => setCity(e.target.value)}
          placeholder="New York, London, Tokyo, Austin..."
          className="w-full mt-2 mb-5 px-4 py-3 bg-[#0a0a0b] border border-white/10 rounded-xl text-white focus:outline-none focus:border-amber-500/50"
          data-testid="planner-city-input"
          required
        />

        <label className="overline text-neutral-500">Vibe</label>
        <div className="flex flex-wrap gap-2 mt-2 mb-5">
          {VIBES.map((v) => (
            <button
              type="button"
              key={v.id}
              onClick={() => setVibe(v.id)}
              className={`px-4 py-2 rounded-full text-sm border ${
                vibe === v.id ? "border-amber-500 text-amber-400 bg-amber-500/10" : "border-white/10 text-neutral-400 hover:text-white"
              }`}
              data-testid={`vibe-${v.id}`}
            >
              {v.label}
            </button>
          ))}
        </div>

        <label className="overline text-neutral-500">Budget</label>
        <div className="flex flex-wrap gap-2 mt-2 mb-6">
          {BUDGETS.map((b) => (
            <button
              type="button"
              key={b.id}
              onClick={() => setBudget(b.id)}
              className={`px-4 py-2 rounded-full text-sm border font-semibold tracking-wider ${
                budget === b.id ? "border-amber-500 text-amber-400 bg-amber-500/10" : "border-white/10 text-neutral-400 hover:text-white"
              }`}
              data-testid={`budget-${b.id}`}
            >
              {b.label}
            </button>
          ))}
        </div>

        <button type="submit" disabled={loading} className="btn-amber inline-flex items-center gap-2 disabled:opacity-60" data-testid="planner-generate-btn">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin"/> Planning your night...</> : <><Wand2 className="w-4 h-4"/> Generate date plan</>}
        </button>
      </form>

      {plan && <PlanView plan={plan} />}

      {history.length > 0 && !plan && (
        <div className="mt-14">
          <h2 className="font-serif text-2xl mb-4">Recent plans</h2>
          <div className="space-y-3">
            {history.slice(0, 5).map((p, i) => (
              <button key={i} onClick={() => setPlan(p)} className="w-full text-left card-surface p-5 hover-lift" data-testid={`history-plan-${i}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-serif text-lg">{p.headline}</div>
                    <div className="text-xs text-neutral-500 mt-1">{p.city} · {p.vibe} · {p.budget}</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-neutral-500"/>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const PlanView = ({ plan }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    className="space-y-6"
    data-testid="plan-view"
  >
    <div className="card-surface p-8">
      <div className="overline text-amber-500 mb-2">YOUR NIGHT</div>
      <h2 className="font-serif text-3xl md:text-4xl leading-tight">{plan.headline}</h2>
      <p className="text-neutral-300 mt-4 italic">{plan.narrative}</p>
      {plan.timing && (
        <div className="flex items-center gap-2 text-sm text-neutral-500 mt-5">
          <Clock className="w-4 h-4 text-amber-500"/> {plan.timing}
        </div>
      )}
    </div>

    <div className="grid md:grid-cols-2 gap-6">
      <SpotCard spot={plan.spot_a} index="A" />
      <SpotCard spot={plan.spot_b} index="B" />
    </div>

    {plan.transition && (
      <div className="card-elevated p-6 border-l-2 border-amber-500">
        <div className="overline text-amber-500 mb-2 flex items-center gap-2"><Sparkles className="w-3.5 h-3.5"/> THE TRANSITION</div>
        <p className="text-neutral-200">{plan.transition}</p>
      </div>
    )}

    <div className="grid md:grid-cols-3 gap-6">
      {plan.outfit && (
        <div className="card-surface p-6">
          <div className="overline text-neutral-500 mb-2">OUTFIT</div>
          <p className="text-neutral-300 text-sm leading-relaxed">{plan.outfit}</p>
        </div>
      )}
      {plan.conversation_starter && (
        <div className="card-surface p-6">
          <div className="overline text-neutral-500 mb-2">OPENING QUESTION</div>
          <p className="text-neutral-300 text-sm italic leading-relaxed">"{plan.conversation_starter}"</p>
        </div>
      )}
      {plan.backup_move && (
        <div className="card-surface p-6">
          <div className="overline text-neutral-500 mb-2">BACKUP MOVE</div>
          <p className="text-neutral-300 text-sm leading-relaxed">{plan.backup_move}</p>
        </div>
      )}
    </div>
  </motion.div>
);

const SpotCard = ({ spot, index }) => (
  <div className="card-surface p-7 flex flex-col" data-testid={`spot-${index.toLowerCase()}`}>
    <div className="flex items-center justify-between mb-3">
      <div className="w-9 h-9 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-amber-400 font-serif text-lg">{index}</div>
      <span className="overline text-neutral-500">{spot.type}</span>
    </div>
    <h3 className="font-serif text-2xl leading-tight">{spot.name}</h3>
    <div className="text-xs text-amber-400 mt-1 inline-flex items-center gap-1"><MapPin className="w-3 h-3"/> {spot.neighborhood}</div>
    <p className="text-neutral-300 mt-4 leading-relaxed">{spot.why}</p>
    {spot.order_suggestion && (
      <p className="text-sm text-neutral-400 mt-3"><Coffee className="w-3.5 h-3.5 inline mr-1 text-amber-500"/> {spot.order_suggestion}</p>
    )}
    {spot.estimated_cost && (
      <p className="text-xs text-neutral-500 mt-2">{spot.estimated_cost}</p>
    )}
    <div className="flex flex-wrap gap-2 mt-5 pt-5 border-t border-white/5">
      <a href={spot.maps_url} target="_blank" rel="noreferrer"
         className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 text-sm text-neutral-200 hover:border-amber-500/40 hover:text-amber-400 transition-colors"
         data-testid={`maps-${index.toLowerCase()}`}>
        <MapPin className="w-4 h-4"/> Open in Maps <ExternalLink className="w-3 h-3"/>
      </a>
      <a href={spot.opentable_url} target="_blank" rel="noreferrer"
         className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500 text-white text-sm hover:bg-amber-400 transition-colors"
         data-testid={`book-${index.toLowerCase()}`}>
        Book on OpenTable <ExternalLink className="w-3 h-3"/>
      </a>
    </div>
  </div>
);

export default DatePlanner;
